// Captures marketing attribution from the URL on the *first* page view of a
// visit and persists it through the session, so the original referrer/UTMs
// stick even if the user navigates around before converting.
//
// First-touch wins: once a UTM is stored, it isn't overwritten unless 30 days
// have passed (a fresh campaign visit). Direct/unattributed visits never
// overwrite an existing first-touch.

const STORAGE_KEY = "frontier_attribution_v1";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const UTM_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
];

function safeGet() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        if (parsed._ts && Date.now() - parsed._ts > MAX_AGE_MS) return null;
        return parsed;
    } catch {
        return null;
    }
}

function safeSet(obj) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch {
        /* private mode / quota — ignore, still works for the current session */
    }
}

/**
 * Reads the current URL for UTM params. If any are present, store as
 * first-touch attribution (or refresh if expired). Should be called once
 * on app mount. UTMs remain visible in the URL bar so marketing/analytics
 * platforms (and your content manager) can still see them.
 */
export function captureAttribution() {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const incoming = {};
    let hasUtm = false;
    for (const key of UTM_KEYS) {
        const v = params.get(key);
        if (v) {
            incoming[key] = v;
            hasUtm = true;
        }
    }

    const existing = safeGet();
    if (hasUtm && !existing) {
        // First-touch — capture everything.
        safeSet({
            ...incoming,
            referrer: document.referrer || "",
            landing_page: window.location.href.split("#")[0],
            _ts: Date.now(),
        });
    }
    // URL is intentionally left untouched — UTMs stay visible.
}

/**
 * Returns the captured attribution payload (or empty object) ready to send
 * with a subscribe request. Defaults to utm_source="direct" when no UTM was
 * ever captured and there's no referrer, so direct visits are clearly
 * distinguishable from missing/null data in Kit.
 */
export function getAttribution() {
    const stored = safeGet() || {};
    const out = {};
    for (const key of [...UTM_KEYS, "referrer", "landing_page"]) {
        if (stored[key]) out[key] = stored[key];
    }
    // Always include referrer + landing for the current session.
    if (!out.referrer && typeof document !== "undefined" && document.referrer) {
        out.referrer = document.referrer;
    }
    if (!out.landing_page && typeof window !== "undefined") {
        out.landing_page = window.location.href.split("#")[0];
    }
    // If we have nothing campaign-related and no referrer, flag as direct.
    if (!out.utm_source && !out.referrer) {
        out.utm_source = "direct";
    }
    return out;
}
